<template>
  <el-dialog
    :visible.sync="dialogVisible"
    custom-class="live-camera-dialog"
    width="880px"
    :show-close="true"
    :append-to-body="true"
    :destroy-on-close="true"
    :close-on-click-modal="!switching"
    @close="handleClose"
  >
    <template slot="title">
      <div class="live-camera-dialog__title">
        <span class="live-camera-dialog__title-text">{{ title }}</span>
        <template v-if="licensePlateHtml">
          <span class="live-camera-dialog__title-sep" aria-hidden="true">
            ·
          </span>
          <span
            class="live-camera-dialog__title-plate"
            v-html="licensePlateHtml"
          ></span>
        </template>
      </div>
    </template>

    <div class="live-camera-dialog__body">
      <div class="live-camera-dialog__segments" role="tablist">
        <button
          v-for="option in channelOptions"
          :key="option.value"
          type="button"
          role="tab"
          class="live-camera-dialog__segment"
          :class="{ 'is-active': channel === option.value }"
          :aria-selected="channel === option.value"
          :disabled="switching"
          @click="selectChannel(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div
        class="live-camera-dialog__stage"
        :class="{ 'is-dual': streams.length > 1 }"
      >
        <div
          v-if="!streams.length && !switching"
          class="live-camera-dialog__empty"
        >
          <i class="el-icon-video-camera"></i>
          <span>{{ $t('CAMERA_SELECT_HINT') }}</span>
        </div>

        <div v-if="switching" class="live-camera-dialog__status">
          <i class="el-icon-loading"></i>
          <span>{{ $t('CAMERA_CONNECTING') }}</span>
        </div>

        <div
          v-if="streams.length"
          class="live-camera-dialog__players"
          :class="{ 'is-dual': streams.length > 1 }"
        >
          <div
            v-for="stream in streams"
            :key="stream.key"
            class="live-camera-dialog__player"
          >
            <video
              ref="players"
              class="live-camera-dialog__video"
              controls
              autoplay
              playsinline
            ></video>
            <div v-if="streams.length > 1" class="live-camera-dialog__name">
              {{ stream.label }}
            </div>
            <div v-if="!switching" class="live-camera-dialog__live">
              <span class="live-camera-dialog__dot"></span>
              {{ $t('LIVE') }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="live-camera-dialog__error">
        <p>{{ errorMessage }}</p>
        <el-button
          size="mini"
          type="primary"
          :loading="switching"
          @click="retry"
        >
          {{ $t('RETRY') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import Hls from 'hls.js'
import config from '@/configs/configs.js'
import { cameraLive } from '@/api/cars'

export default {
  name: 'LiveCameraDialog',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    licensePlateHtml: {
      type: String,
      default: '',
    },
    vehicleId: {
      type: [String, Number],
      default: null,
    },
    imei: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hlsPlayers: [],
      errorMessage: '',
      channel: '',
      activeChannel: '',
      dialogVisible: this.show,
      switching: false,
      pending: null,
      closing: false,
      retryKind: '',
      failedSwitch: null,
      streamWaitTimer: null,
      resolveStreamWait: null,
    }
  },
  computed: {
    channelOptions() {
      return [
        { value: 'out', label: this.$t('CAMERA_OUT') },
        { value: 'in', label: this.$t('CAMERA_IN') },
        { value: 'inout', label: this.$t('CAMERA_BOTH') },
      ]
    },
    streams() {
      if (!this.imei || !this.activeChannel) return []
      if (this.activeChannel === 'out') {
        return [
          {
            key: 'out',
            url: this.streamUrl(0),
            label: this.$t('CAMERA_OUT'),
          },
        ]
      }
      if (this.activeChannel === 'in') {
        return [
          {
            key: 'in',
            url: this.streamUrl(1),
            label: this.$t('CAMERA_IN'),
          },
        ]
      }
      if (this.activeChannel === 'inout') {
        return [
          {
            key: 'out',
            url: this.streamUrl(0),
            label: this.$t('CAMERA_OUT'),
          },
          {
            key: 'in',
            url: this.streamUrl(1),
            label: this.$t('CAMERA_IN'),
          },
        ]
      }
      return []
    },
  },
  methods: {
    streamUrl(index) {
      return `${config.cameraLiveBase}/${index}/${this.imei}.m3u8`
    },
    selectChannel(next) {
      if (this.switching || next === this.channel) return
      this.channel = next
      this.onChannelChange(next)
    },
    retry() {
      if (this.switching) return
      if (this.retryKind === 'stream') {
        this.errorMessage = ''
        this.retryKind = ''
        this.destroyPlayers()
        this.setupPlayers()
        return
      }
      const next = this.failedSwitch ? this.failedSwitch.next : this.channel
      if (!next) return
      this.onChannelChange(next)
    },
    async onChannelChange(next) {
      const previous = this.activeChannel
      if (previous === next) return

      this.switching = true
      this.errorMessage = ''
      this.retryKind = ''
      this.destroyPlayers()

      this.pending = this.switchChannel(previous, next)
      try {
        await this.pending
      } finally {
        this.pending = null
        this.switching = false
      }
    },
    async switchChannel(previous, next) {
      if (previous && this.vehicleId) {
        const stopped = await cameraLive.api(this.vehicleId, {
          action: 'stop',
          channel: previous,
        })

        if (!stopped) {
          if (!this.closing) this.markRetry('switch', previous, next)
          return
        }

        this.activeChannel = ''
      }

      if (this.closing || !next) {
        this.activeChannel = ''
        return
      }

      const started = await cameraLive.api(this.vehicleId, {
        action: 'start',
        channel: next,
      })

      if (this.closing) {
        if (started) {
          await cameraLive.api(this.vehicleId, {
            action: 'stop',
            channel: next,
          })
        }
        this.activeChannel = ''
        return
      }

      if (!started) {
        this.markRetry('switch', '', next)
        return
      }

      this.activeChannel = next
      this.failedSwitch = null
      await this.waitBeforeStream()
      if (this.closing || this.activeChannel !== next) return
      this.setupPlayers()
    },
    waitBeforeStream() {
      this.clearStreamWait()
      return new Promise((resolve) => {
        this.resolveStreamWait = resolve
        this.streamWaitTimer = setTimeout(() => {
          this.streamWaitTimer = null
          this.resolveStreamWait = null
          resolve()
        }, 15000)
      })
    },
    clearStreamWait() {
      if (this.streamWaitTimer) {
        clearTimeout(this.streamWaitTimer)
        this.streamWaitTimer = null
      }
      if (this.resolveStreamWait) {
        const resolve = this.resolveStreamWait
        this.resolveStreamWait = null
        resolve()
      }
    },
    markRetry(kind, previous, next) {
      this.errorMessage = this.$t('LIVE_CAMERA_UNAVAILABLE')
      this.retryKind = kind
      this.failedSwitch = { previous, next }
    },
    async handleClose() {
      if (this.closing) return
      this.closing = true
      this.clearStreamWait()
      this.destroyPlayers()

      if (this.pending) {
        await this.pending
      }

      const channel = this.activeChannel
      this.activeChannel = ''
      this.channel = ''

      if (channel && this.vehicleId) {
        await cameraLive.api(this.vehicleId, {
          action: 'stop',
          channel,
        })
      }

      this.closing = false
      this.$emit('close')
      this.$emit('update:show', false)
    },
    setupPlayers() {
      this.$nextTick(() => {
        const videos = this.$refs.players
        const list = Array.isArray(videos) ? videos : videos ? [videos] : []

        this.streams.forEach((stream, index) => {
          const video = list[index]
          if (video) this.attachPlayer(video, stream.url)
        })
      })
    },
    attachPlayer(video, url) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: false,
          enableWorker: true,
          liveSyncDurationCount: 2,
          liveMaxLatencyDurationCount: 8,
          maxBufferLength: 12,
          maxMaxBufferLength: 24,
          backBufferLength: 10,
          startFragPrefetch: true,
          manifestLoadingTimeOut: 20000,
          manifestLoadingMaxRetry: 6,
          manifestLoadingRetryDelay: 1000,
          levelLoadingTimeOut: 20000,
          levelLoadingMaxRetry: 6,
          levelLoadingRetryDelay: 1000,
          fragLoadingTimeOut: 30000,
          fragLoadingMaxRetry: 8,
          fragLoadingRetryDelay: 1500,
          fragLoadingMaxRetryTimeout: 8000,
        })
        hls.loadSource(url)
        hls.attachMedia(video)
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            this.markRetry('stream', this.activeChannel, this.channel)
            hls.destroy()
            this.hlsPlayers = this.hlsPlayers.filter((player) => player !== hls)
          }
        })
        this.hlsPlayers.push(hls)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.onerror = () => {
          this.markRetry('stream', this.activeChannel, this.channel)
        }
        video.src = url
      } else {
        this.markRetry('stream', this.activeChannel, this.channel)
      }
    },
    destroyPlayers() {
      this.hlsPlayers.forEach((hls) => hls.destroy())
      this.hlsPlayers = []

      const videos = this.$refs.players
      const list = Array.isArray(videos) ? videos : videos ? [videos] : []
      list.forEach((video) => {
        video.onerror = null
        video.pause()
        video.removeAttribute('src')
        video.load()
      })
    },
    resetState() {
      this.channel = ''
      this.activeChannel = ''
      this.errorMessage = ''
      this.switching = false
      this.closing = false
      this.retryKind = ''
      this.failedSwitch = null
      this.clearStreamWait()
    },
  },
  watch: {
    show(value) {
      this.dialogVisible = value
      if (value) {
        this.resetState()
      } else {
        this.destroyPlayers()
      }
    },
  },
  beforeDestroy() {
    this.closing = true
    this.clearStreamWait()
    this.destroyPlayers()
    if (this.activeChannel && this.vehicleId) {
      cameraLive.api(this.vehicleId, {
        action: 'stop',
        channel: this.activeChannel,
      })
    }
  },
}
</script>

<style lang="scss">
.live-camera-dialog {
  max-width: 95vw;
  overflow: hidden;
  border: none !important;

  .el-dialog__body {
    padding: 16px !important;
    overflow: hidden;
  }

  .el-dialog__header {
    padding: 14px 18px 10px;
  }

  .el-dialog__headerbtn {
    top: 16px;
  }
}

.live-camera-dialog__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  padding-left: 28px;
  line-height: 1.4;
}

.live-camera-dialog__title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.live-camera-dialog__title-sep {
  color: #c0c4cc;
  font-weight: 500;
}

.live-camera-dialog__title-plate {
  font-size: 14px;

  strong {
    font-weight: 600;
  }
}

.live-camera-dialog__body {
  overflow: hidden;
}

.live-camera-dialog__segments {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.live-camera-dialog__segment {
  flex: 1;
  height: 36px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #303133;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled):not(.is-active) {
    border-color: #ff5604;
    color: #ff5604;
  }

  &.is-active {
    border-color: #ff5604;
    background: #ff5604;
    color: #fff;
    font-weight: 600;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.live-camera-dialog__stage {
  position: relative;
  height: 405px;
  overflow: hidden;
  background: #111;
}

.live-camera-dialog__empty,
.live-camera-dialog__status {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #c0c4cc;
  font-size: 14px;

  i {
    font-size: 28px;
  }
}

.live-camera-dialog__status {
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
}

.live-camera-dialog__players {
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
}

.live-camera-dialog__players.is-dual {
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.live-camera-dialog__player {
  position: relative;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  background: #000;
}

.live-camera-dialog__name,
.live-camera-dialog__live {
  position: absolute;
  top: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  line-height: 1;
}

.live-camera-dialog__name {
  inset-inline-start: 10px;
}

.live-camera-dialog__live {
  inset-inline-end: 10px;
}

.live-camera-dialog__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f56c6c;
  box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.7);
  animation: live-camera-pulse 1.4s infinite;
}

@keyframes live-camera-pulse {
  70% {
    box-shadow: 0 0 0 6px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}

.live-camera-dialog__video {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  object-fit: contain;
  background: #000;
}

.live-camera-dialog__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;

  p {
    margin: 0;
    color: #f56c6c;
    text-align: center;
  }
}

@media (max-width: 768px) {
  .live-camera-dialog__stage {
    height: 360px;
  }

  .live-camera-dialog__stage.is-dual {
    height: 620px;
  }

  .live-camera-dialog__players.is-dual {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 12px;
  }

  .live-camera-dialog__players.is-dual .live-camera-dialog__player {
    height: auto;
    min-height: 0;
  }
}
</style>
